import React, { useState, useEffect, useRef } from 'react';

const TimePicker = ({ value, onChange, name, id, className, required, error, placeholder = '--:--' }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [hour, setHour] = useState('12');
  const [minute, setMinute] = useState('00');
  const [amPm, setAmPm] = useState('AM');
  const pickerRef = useRef(null);
  const hourRef = useRef(null);
  const minuteRef = useRef(null);

  // Parse 24-hour format time string (HH:MM) to 12-hour format
  useEffect(() => {
    if (value) {
      try {
        const [hours, minutes] = value.split(':');
        const hour24 = parseInt(hours, 10);
        const min = minutes || '00';
        
        if (hour24 === 0) {
          setHour('12');
          setAmPm('AM');
        } else if (hour24 === 12) {
          setHour('12');
          setAmPm('PM');
        } else if (hour24 > 12) {
          setHour(String(hour24 - 12).padStart(2, '0'));
          setAmPm('PM');
        } else {
          setHour(String(hour24).padStart(2, '0'));
          setAmPm('AM');
        }
        setMinute(min);
      } catch (error) {
        console.error('Error parsing time:', error);
      }
    } else {
      setHour('12');
      setMinute('00');
      setAmPm('AM');
    }
  }, [value]);

  // Convert 12-hour format to 24-hour format (HH:MM)
  const convertTo24Hour = (h, m, ap) => {
    let hour24 = parseInt(h, 10);
    if (ap === 'PM' && hour24 !== 12) {
      hour24 += 12;
    } else if (ap === 'AM' && hour24 === 12) {
      hour24 = 0;
    }
    return `${String(hour24).padStart(2, '0')}:${m}`;
  };

  // Handle hour change
  const handleHourChange = (newHour) => {
    setHour(newHour);
    const time24 = convertTo24Hour(newHour, minute, amPm);
    onChange({ target: { name, value: time24 } });
  };

  // Handle minute change
  const handleMinuteChange = (newMinute) => {
    setMinute(newMinute);
    const time24 = convertTo24Hour(hour, newMinute, amPm);
    onChange({ target: { name, value: time24 } });
  };

  // Handle AM/PM change
  const handleAmPmChange = (newAmPm) => {
    setAmPm(newAmPm);
    const time24 = convertTo24Hour(hour, minute, newAmPm);
    onChange({ target: { name, value: time24 } });
  };

  // Format display time
  const getDisplayTime = () => {
    if (!value) return '';
    try {
      const [hours, minutes] = value.split(':');
      const hour24 = parseInt(hours, 10);
      const min = minutes || '00';
      
      let displayHour = hour24;
      let displayAmPm = 'AM';
      
      if (hour24 === 0) {
        displayHour = 12;
        displayAmPm = 'AM';
      } else if (hour24 === 12) {
        displayHour = 12;
        displayAmPm = 'PM';
      } else if (hour24 > 12) {
        displayHour = hour24 - 12;
        displayAmPm = 'PM';
      } else {
        displayAmPm = 'AM';
      }
      
      return `${String(displayHour).padStart(2, '0')}:${min} ${displayAmPm}`;
    } catch (error) {
      return value;
    }
  };

  // Auto-scroll to selected values when picker opens
  useEffect(() => {
    if (isOpen) {
      // Scroll to selected hour
      setTimeout(() => {
        if (hourRef.current) {
          const selectedHourElement = hourRef.current.querySelector(`[data-hour="${hour}"]`);
          if (selectedHourElement) {
            selectedHourElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        }
      }, 10);

      // Scroll to selected minute
      setTimeout(() => {
        if (minuteRef.current) {
          const selectedMinuteElement = minuteRef.current.querySelector(`[data-minute="${minute}"]`);
          if (selectedMinuteElement) {
            selectedMinuteElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        }
      }, 20);
    }
  }, [isOpen, hour, minute]);

  // Close picker when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (pickerRef.current && !pickerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  // Generate hour options (1-12)
  const hourOptions = Array.from({ length: 12 }, (_, i) => i + 1).map(h => 
    String(h).padStart(2, '0')
  );

  // Generate minute options (0-59)
  const minuteOptions = Array.from({ length: 60 }, (_, i) => 
    String(i).padStart(2, '0')
  );

  return (
    <div ref={pickerRef} style={{ position: 'relative', width: '100%' }}>
      <div
        onClick={() => setIsOpen(!isOpen)}
        style={{
          display: 'flex',
          alignItems: 'center',
          cursor: 'pointer',
          position: 'relative'
        }}
      >
        <input
          type="text"
          id={id}
          name={name}
          value={getDisplayTime()}
          readOnly
          placeholder={placeholder}
          required={required}
          className={className}
          style={{
            padding: '6px 35px 6px 10px',
            fontSize: '14px',
            height: '36px',
            width: '100%',
            cursor: 'pointer',
            backgroundColor: error ? '#fff5f5' : 'white',
            border: error ? '1px solid #dc3545' : '1px solid #ced4da',
            borderRadius: '4px'
          }}
        />
        <i
          className="fas fa-clock"
          style={{
            position: 'absolute',
            right: '12px',
            color: '#6b7280',
            pointerEvents: 'none'
          }}
        />
      </div>

      {isOpen && (
        <div
          style={{
            position: 'absolute',
            top: '100%',
            left: 0,
            zIndex: 1000,
            backgroundColor: 'white',
            border: '1px solid #ced4da',
            borderRadius: '4px',
            boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
            marginTop: '4px',
            padding: '10px',
            minWidth: '280px',
            display: 'flex',
            gap: '10px',
            alignItems: 'center'
          }}
        >
          {/* Hours Column */}
          <div
            ref={hourRef}
            style={{
              flex: 1,
              maxHeight: '200px',
              overflowY: 'auto',
              border: '1px solid #e5e7eb',
              borderRadius: '4px'
            }}
          >
            {hourOptions.map((h) => (
              <div
                key={h}
                data-hour={h}
                onClick={() => handleHourChange(h)}
                style={{
                  padding: '8px 12px',
                  cursor: 'pointer',
                  backgroundColor: hour === h ? '#0094a9' : 'white',
                  color: hour === h ? 'white' : '#374151',
                  textAlign: 'center',
                  fontSize: '14px',
                  borderBottom: '1px solid #e5e7eb',
                  transition: 'background-color 0.2s'
                }}
                onMouseEnter={(e) => {
                  if (hour !== h) {
                    e.target.style.backgroundColor = '#f3f4f6';
                  }
                }}
                onMouseLeave={(e) => {
                  if (hour !== h) {
                    e.target.style.backgroundColor = 'white';
                  }
                }}
              >
                {h}
              </div>
            ))}
          </div>

          <span style={{ fontSize: '18px', fontWeight: 'bold', color: '#374151' }}>:</span>

          {/* Minutes Column */}
          <div
            ref={minuteRef}
            style={{
              flex: 1,
              maxHeight: '200px',
              overflowY: 'auto',
              border: '1px solid #e5e7eb',
              borderRadius: '4px'
            }}
          >
            {minuteOptions.map((m) => (
              <div
                key={m}
                data-minute={m}
                onClick={() => handleMinuteChange(m)}
                style={{
                  padding: '8px 12px',
                  cursor: 'pointer',
                  backgroundColor: minute === m ? '#0094a9' : 'white',
                  color: minute === m ? 'white' : '#374151',
                  textAlign: 'center',
                  fontSize: '14px',
                  borderBottom: '1px solid #e5e7eb',
                  transition: 'background-color 0.2s'
                }}
                onMouseEnter={(e) => {
                  if (minute !== m) {
                    e.target.style.backgroundColor = '#f3f4f6';
                  }
                }}
                onMouseLeave={(e) => {
                  if (minute !== m) {
                    e.target.style.backgroundColor = 'white';
                  }
                }}
              >
                {m}
              </div>
            ))}
          </div>

          {/* AM/PM Column */}
          <div
            style={{
              flex: 0.8,
              maxHeight: '200px',
              overflowY: 'auto',
              border: '1px solid #e5e7eb',
              borderRadius: '4px'
            }}
          >
            {['AM', 'PM'].map((ap) => (
              <div
                key={ap}
                onClick={() => handleAmPmChange(ap)}
                style={{
                  padding: '8px 12px',
                  cursor: 'pointer',
                  backgroundColor: amPm === ap ? '#0094a9' : 'white',
                  color: amPm === ap ? 'white' : '#374151',
                  textAlign: 'center',
                  fontSize: '14px',
                  borderBottom: '1px solid #e5e7eb',
                  transition: 'background-color 0.2s'
                }}
                onMouseEnter={(e) => {
                  if (amPm !== ap) {
                    e.target.style.backgroundColor = '#f3f4f6';
                  }
                }}
                onMouseLeave={(e) => {
                  if (amPm !== ap) {
                    e.target.style.backgroundColor = 'white';
                  }
                }}
              >
                {ap}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default TimePicker;

